import { Link } from "react-router-dom";
import { Loader } from "lucide-react";
import { getPositionDisplay } from "../../utils/positionFormatter";
import { FaArrowRight } from "react-icons/fa";
import { clubInitials } from "../../utils/clubInitials";

// Pad a count to a 2-digit "04" string. Leaves 10+ as-is. Matches the
// Club card style.
const padCount = (n) => {
  const num = Number(n) || 0;
  return num < 10 ? `0${num}` : String(num);
};

// Short relative-ish timestamp for the latest pitch row, e.g.
// "Fri, May 22 · 03:06 PM".
const formatActivityDate = (dateString) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Status pill next to the position title — colour follows the pitch
// state coming back from the backend.
function StatusPill({ status }) {
  if (!status) return null;
  const s = String(status).toLowerCase();
  let bg = "rgba(250, 170, 0, 0.18)";
  let fg = "#A66A00";
  let label = s.charAt(0).toUpperCase() + s.slice(1);
  if (s === "accepted") {
    bg = "#E6FFEB";
    fg = "#006849";
    label = "Accepted";
  } else if (s === "rejected") {
    bg = "rgba(255, 77, 79, 0.18)";
    fg = "#B91C1C";
    label = "Rejected";
  } else if (s === "active") {
    label = "Under review";
  }
  return (
    <span
      className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-wide"
      style={{ background: bg, color: fg }}
    >
      {label}
    </span>
  );
}

const AgentRecentPitches = ({ recentActivity, recentLoading }) => {
  // ── Loading skeleton (matches the new card layout) ──────────────
  if (recentLoading) {
    return (
      <div className="bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-5 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]">
        <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
        <div className="flex items-center gap-4">
          <div className="h-6 w-56 bg-gray-200 rounded animate-pulse" />
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex items-center gap-6">
          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-px bg-[#E5E5E5]" />
        <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
        <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
        <div className="h-10 w-44 border border-[#E6E6E6] rounded-md animate-pulse" />
      </div>
    );
  }

  const totalPitches = Number(recentActivity?.total_pitches) || 0;
  const acceptedPitches = Number(recentActivity?.accepted_pitches) || 0;
  const rejectionRate = recentActivity?.rejection_rate || 0;
  const firstActivity = recentActivity?.recent_activity?.[0] || null;

  // ── Empty state (no pitches yet) ─────────────────────────────────
  if (totalPitches === 0 && !firstActivity) {
    return (
      <div className="bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-4 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]">
        <span className="text-[12px] font-medium tracking-wider uppercase text-[#929292]">
          Recently Applied Pitch
        </span>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 py-6">
          <p className="text-[#232323] text-base md:text-lg font-medium text-center">
            You haven't applied to any requests yet.
          </p>
          <Link
            to="/agent/tenders"
            className="inline-flex items-center gap-2 bg-[#2283FF] text-white px-5 py-2.5 rounded-lg hover:bg-blue-600 font-medium text-sm"
          >
            Browse requests
            <FaArrowRight className="text-white" />
          </Link>
        </div>
      </div>
    );
  }

  // ── Loaded — mirrors ClubRecentTenders layout ────────────────────
  const positionLabel = firstActivity
    ? getPositionDisplay(firstActivity.player, firstActivity.player?.position) || "—"
    : "Your pitches";
  const activityDate = firstActivity ? formatActivityDate(firstActivity.updated_at) : null;
  const clubName = firstActivity?.club?.name;
  const clubLogo = firstActivity?.club?.logo;

  return (
    <div className="bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-5 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]">
      {/* Small grey header */}
      <span className="text-[12px] font-medium tracking-wider uppercase text-[#929292]">
        Recently Applied Pitch
      </span>

      {/* Title row: position name + status pill */}
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="text-xl font-bold text-[#242426] leading-tight m-0">
          {positionLabel}
        </h2>
        {firstActivity?.status && <StatusPill status={firstActivity.status} />}
      </div>

      {/* Club + timestamp row */}
      <div className="flex items-center gap-3 min-w-0">
        {/* A club only has `club.logo` when it is linked to a Wyscout team, so
            for a self-registered club this branch is the NORMAL case, not an
            edge case. It used to paint a bare grey disc — a hole in the card
            where the crest belongs. It now carries the club's initials, the
            same badge TenderCard.js:571-578 and TenderDetailsModal.js:144-154
            already show for the same missing crest. */}
        {clubLogo ? (
          <img
            src={clubLogo}
            alt={clubName || "Club"}
            className="w-10 h-10 object-contain flex-shrink-0 rounded-full bg-gray-50"
          />
        ) : (
          <div
            className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#E9EEF6] text-[13px] font-bold text-[#6B7280]"
            aria-label={clubName ? `${clubName} crest` : "Club crest"}
          >
            {clubInitials(clubName)}
          </div>
        )}
        <div className="flex flex-col min-w-0">
          <span className="font-medium text-base text-black truncate">
            {clubName ? `By ${clubName}` : "—"}
          </span>
          {activityDate && (
            <span className="text-xs text-[#777777]">{activityDate}</span>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-[#E5E5E5]" />

      {/* Two count banners */}
      <div className="flex flex-col gap-4">
        <div className="bg-[#E6FFEB] border border-[#B3FFC0] rounded-xl px-4 py-2.5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[1px] text-[#006849]">
            Total applied pitches
          </span>
          <span className="text-base font-bold text-[#006849]">
            {padCount(totalPitches)}
          </span>
        </div>
        <div className="bg-[#E6F3FF] border border-[#B3D4FF] rounded-xl px-4 py-2.5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[1px] text-[#0019A6]">
            Accepted pitches
          </span>
          <span className="text-base font-bold text-[#0019A6]">
            {padCount(acceptedPitches)}
          </span>
        </div>
        <div className="bg-[#F1F5F9] border border-[#D6DBEA] rounded-xl px-4 py-2.5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[1px] text-[#5B6472]">
            Rejection rate
          </span>
          <span className="text-base font-bold text-[#232323]">
            {rejectionRate}%
          </span>
        </div>
      </div>

      {/* Go to applied pitches button */}
      <Link
        to="/agent/tenders"
        state={{ activeTab: "applied" }}
        className="inline-flex items-center justify-center gap-2 self-start bg-white border border-[#E6E6E6] rounded-md px-3 py-2.5 text-[#1E1E1E] text-base hover:bg-gray-50 transition"
      >
        Go to applied pitches
        <FaArrowRight className="text-[#1E1E1E] text-xs" />
      </Link>
    </div>
  );
};

export default AgentRecentPitches;
